import { useApolloClient } from "@apollo/client";
import { CircularProgress, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import {
  EstimatingMethod,
  Proposal,
  ProposalProduct,
  ProposalStage,
  ProposalStatus,
} from "generated/graphql";
import useCustomGetProposal from "hooks/proposals/useCustomGetProposal";
import useCustomAddProposalStage from "hooks/proposals/useCustomAddProposalStage";
import useDeleteProposalStage from "hooks/proposals/useDeleteProposalStage";
import useModalState from "hooks/useModalState";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import PendingChanges from "modules/PendingChanges/PendingChanges";
import ProposalContext from "modules/proposals/providers/ProposalContextProvider";
import ProposalFormsContextProvider from "modules/proposals/providers/ProposalFormsContextProvider";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import DraftContext from "modules/proposals/draft/DraftContext";
import ReorderStagesDrawer from "modules/proposals/draft/ReorderStagesDrawer";
import Stage from "modules/proposals/draft/Stage";
import TopPanel from "modules/proposals/draft/TopPanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { getRoute } from "utils/routing";
import proposalProductFragment from "schema/fragments/proposalProductFragment";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import TourHelpButton from "components/PageTour/TourHelpButton";
import PageTourContext from "components/PageTour/PageTourContext";
import {
  FIRST_PROPOSAL_TOUR_DRAFT_OFFSET,
  FIRST_PROPOSAL_TOUR_TOTAL_STEPS,
} from "modules/proposals/firstProposalTour";
import createProposalsDraftPageTourSteps from "./CreateProposalsDraftPage.tour";
import proposalsDraftPageTourSteps from "./ProposalsDraftPage.tour";

export default function ProposalDraftPage({ forceReadOnly = false }: { forceReadOnly?: boolean }) {
  const { proposalId } = useParams();
  const tourRef = useRef<PageTourHandle>(null);
  const editTourRef = useRef<PageTourHandle>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const tourContextValue = useMemo(
    () => ({
      advanceFrom: (stepId: string) => {
        tourRef.current?.advanceFrom(stepId);
        editTourRef.current?.advanceFrom(stepId);
      },
    }),
    []
  );
  const client = useApolloClient();
  const { loading, data: proposal } = useCustomGetProposal({ id: proposalId });
  const [expandedStageId, setExpandedStageId] = useState<string | false>(false);
  const [expandedProductByStage, setExpandedProductByStage] = useState<
    Record<string, string | false>
  >({});
  const [addProposalStage] = useCustomAddProposalStage({ proposalId: proposalId ?? "" });
  const [deleteProposalStage] = useDeleteProposalStage();
  const reorderDrawer = useModalState();

  const handleDeleteStage = useCallback(
    async (id: string) => {
      await deleteProposalStage({ variables: { id } });
    },
    [deleteProposalStage]
  );

  const writeProposalProduct = useCallback(
    (proposalProduct: ProposalProduct) => {
      client.writeFragment({
        id: `ProposalProduct:${proposalProduct.id}`,
        fragment: proposalProductFragment,
        data: proposalProduct,
      });
    },
    [client]
  );

  const handleAddStage = useCallback(async () => {
    if (!proposalId) return;
    const result = await addProposalStage({ variables: { proposalId } });
    if (result?.data) {
      const stages = (result.data as any)?.customAddProposalStage?.stages;
      const newStage = stages?.[stages.length - 1];
      if (newStage) setExpandedStageId(newStage.id);
    }
  }, [addProposalStage, proposalId]);

  const stages = proposal?.stages ?? [];
  const isReadOnly = forceReadOnly || proposal?.status !== ProposalStatus.DRAFT;
  const isWorksheet = proposal?.estimatingMethod === EstimatingMethod.WORKSHEET;

  const isProposalLoaded = !loading && !!proposal;

  useEffect(() => {
    if (!isProposalLoaded) return;
    if ((location.state as { startTour?: boolean })?.startTour) {
      tourRef.current?.replay();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProposalLoaded, location.state]);

  useEffect(() => {
    if (stages.length === 1 && expandedStageId === false) {
      const stage = stages[0];
      if (stage.name) setExpandedStageId(stage.id);
    }
  }, [stages]);

  if (loading || !proposal) {
    return (
      <DashboardPage>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "60vh" }}
        >
          <CircularProgress size={40} />
        </MDBox>
      </DashboardPage>
    );
  }

  const activeExpandedStage = expandedStageId;

  return (
    <DashboardPage>
      <DraftContext.Provider value={{ isReadOnly }}>
        <ProposalFormsContextProvider>
          <ProposalContext.Provider
            value={{
              proposal: proposal as Proposal,
              writeProposalProduct,
              inViewMode: isReadOnly,
            }}
          >
            <PageTourContext.Provider value={tourContextValue}>
              <MDBox display="flex" justifyContent="flex-end">
                <TourHelpButton onClick={() => editTourRef.current?.replay()} />
              </MDBox>
              <MDBox sx={{ backgroundColor: draftColors.pageBg, minHeight: "100%" }}>
                <PageTour
                  ref={tourRef}
                  tourKey="createProposalsDraftPage"
                  steps={createProposalsDraftPageTourSteps}
                  stepOffset={FIRST_PROPOSAL_TOUR_DRAFT_OFFSET}
                  totalSteps={FIRST_PROPOSAL_TOUR_TOTAL_STEPS}
                />
                <PageTour
                  ref={editTourRef}
                  tourKey="proposalsDraftPage"
                  steps={proposalsDraftPageTourSteps}
                />
                <MDBox mb={3}>
                  <TopPanel
                    proposal={proposal}
                    onClientInfoEditClick={() => tourRef.current?.next()}
                  />
                </MDBox>

                {stages.map((stage) => (
                  <MDBox key={`${stage.id}:${expandedProductByStage[stage.id] ?? ""}`} mb={2}>
                    <Stage
                      stage={stage as ProposalStage}
                      proposalId={proposal.id}
                      availableStages={stages as ProposalStage[]}
                      isExpanded={activeExpandedStage === stage.id}
                      isWorksheet={isWorksheet}
                      onToggleExpand={() =>
                        setExpandedStageId((prev) => (prev === stage.id ? false : stage.id))
                      }
                      onRequestExpand={() => setExpandedStageId(stage.id)}
                      initialExpandedProductId={expandedProductByStage[stage.id] ?? false}
                      onExpandProduct={(stageId, productId) => {
                        setExpandedStageId(stageId);
                        setExpandedProductByStage((prev) => ({
                          ...prev,
                          [stageId]: productId,
                        }));
                      }}
                      onStageDuplicated={(newStageId) => {
                        setExpandedStageId(newStageId);
                      }}
                      onDeleteStage={stages.length > 1 ? handleDeleteStage : undefined}
                    />
                  </MDBox>
                ))}

                {!isReadOnly && (
                  <MDBox display="flex" justifyContent="flex-end" gap="8px" p="12px">
                    {stages.length > 1 && (
                      <UiButton
                        uiVariant="primary-grey"
                        startIcon={<Icon>swap_vert</Icon>}
                        onClick={reorderDrawer.onOpen}
                      >
                        Reorder Jobs
                      </UiButton>
                    )}
                    <UiButton
                      className="tour-add-job-button"
                      uiVariant="primary"
                      startIcon={<Icon>add</Icon>}
                      onClick={handleAddStage}
                    >
                      Add Job
                    </UiButton>
                  </MDBox>
                )}

                <ReorderStagesDrawer
                  open={reorderDrawer.open}
                  onClose={reorderDrawer.onClose}
                  stages={stages as ProposalStage[]}
                  proposalId={proposalId ?? ""}
                />

                <MDBox mt={2} pb={1}>
                  <UiButton
                    uiVariant="outlined"
                    component={Link}
                    to={getRoute("proposals.draft-classic", [["proposalId", proposalId ?? ""]])}
                  >
                    Open classic draft page
                  </UiButton>
                </MDBox>

                {!isReadOnly && <PendingChanges />}
              </MDBox>
            </PageTourContext.Provider>
          </ProposalContext.Provider>
        </ProposalFormsContextProvider>
      </DraftContext.Provider>
    </DashboardPage>
  );
}
